/**
 * Custom C1 Chat Widget
 * 
 * Uses lower-level C1Component for full control over the chat interface.
 * Built following official Thesys guides.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { C1Component, ThemeProvider } from '@thesysai/genui-sdk';
import '@crayonai/react-ui/styles/index.css';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { adaptMuiThemeToCrayon } from '@app/features/thesysChat/theme';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface CustomC1ChatWidgetProps {
  apiUrl: string;
}

export const CustomC1ChatWidget: React.FC<CustomC1ChatWidgetProps> = ({ apiUrl }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const muiTheme = useMuiTheme();
  const crayonTheme = adaptMuiThemeToCrayon(muiTheme);

  // Helper to extract and parse C1 content from thesys wrapper
  const parseC1Response = (rawContent: string): string => {
    // Extract content from <content thesys="true">...</content> tags
    const match = rawContent.match(/<content thesys="true">([\s\S]*?)<\/content>/);
    if (match && match[1]) {
      // Decode HTML entities (&quot; → ", etc.)
      const decoded = match[1]
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#39;/g, "'");
      return decoded;
    }
    return rawContent;
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamedContent]);

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setStreamedContent('');

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: {
            role: 'user',
            content: input,
            id: userMessage.id,
          },
          threadId: `thread-${Date.now()}`,
          responseId: `response-${Date.now()}`,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          
          // Parse SSE events
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                // Accumulate just the content text, not the full component JSON
                if (data.content) {
                  fullContent += data.content;
                  setStreamedContent(fullContent);
                }
              } catch (e) {
                // Ignore parse errors
                console.warn('Failed to parse SSE data:', e);
              }
            }
          }
        }
      }

      // Parse and add assistant message with C1 content
      const parsedContent = parseC1Response(fullContent);
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: parsedContent,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setStreamedContent('');
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ThemeProvider 
      theme={crayonTheme.theme} 
      darkTheme={crayonTheme.darkTheme} 
      mode={crayonTheme.mode}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((message) => (
            <Paper
              key={message.id}
              sx={{
                p: 2,
                maxWidth: '80%',
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}
            >
              {message.role === 'assistant' ? (
                <C1Component c1Response={message.content || ''} isStreaming={false} />
              ) : (
                <Typography>{message.content}</Typography>
              )}
            </Paper>
          ))}

          {/* Streaming message */}
          {isStreaming && streamedContent && (
            <Paper
              sx={{
                p: 2,
                maxWidth: '80%',
                alignSelf: 'flex-start',
                bgcolor: 'background.paper',
              }}
            >
              <C1Component c1Response={parseC1Response(streamedContent) || ''} isStreaming={true} />
            </Paper>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isStreaming}
            variant="outlined"
            size="small"
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            sx={{ alignSelf: 'flex-end' }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
